export function WhyInvestorsBuyAsIs() {
  const advantages = [
    {
      icon: 'ri-tools-fill',
      title: 'Renovation Expertise',
      description: 'Professional investors have in-house construction teams, established contractor relationships, and years of experience managing large-scale renovations efficiently.'
    },
    {
      icon: 'ri-price-tag-3-line',
      title: 'Wholesale Material Costs',
      description: 'Bulk purchasing power and trade accounts allow investors to buy materials at 30-50% below retail prices that individual owners pay.'
    },
    {
      icon: 'ri-funds-line',
      title: 'Lower Cost of Capital',
      description: 'Access to institutional financing, private equity, and cash reserves means investors can fund renovations at much lower interest rates.'
    },
    {
      icon: 'ri-time-line',
      title: 'Faster Execution',
      description: 'Experienced teams can complete renovations in half the time it takes individual owners, reducing carrying costs significantly.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Risk Management',
      description: 'Investors spread risk across multiple properties and have systems to handle unexpected issues without financial strain.'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Long-Term Strategy',
      description: 'Investors plan to hold properties for 5-10+ years, allowing them to amortize renovation costs over a longer period and benefit from appreciation.'
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Why Investors Can Profit Where You Can't
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Professional real estate investors have structural advantages that make renovations profitable for them, even when they don't make sense for individual property owners.
          </p>
        </div>

        {/* Advantages Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {advantages.map((advantage, index) => (
            <div key={index} className="bg-gradient-to-br from-teal-50 to-white rounded-2xl p-8 border-2 border-teal-200">
              <div className="w-16 h-16 bg-teal-600 rounded-xl flex items-center justify-center mb-6">
                <i className={`${advantage.icon} text-3xl text-white`}></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">{advantage.title}</h3>
              <p className="text-gray-700 leading-relaxed">{advantage.description}</p>
            </div>
          ))}
        </div>

        {/* Cost Comparison */}
        <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-10 mb-12 border-2 border-gray-200">
          <h3 className="text-3xl font-bold mb-10 text-center">Same Renovation, Different Costs</h3>
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Individual Owner */}
            <div className="bg-white rounded-xl p-8 shadow-lg border-2 border-red-200">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center">
                  <i className="ri-user-line text-2xl text-white"></i>
                </div>
                <h4 className="text-2xl font-bold">Individual Owner</h4>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Materials (retail pricing)</span>
                  <strong className="text-red-600">$300,000</strong>
                </div>
                <div className="flex justify-between pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Labor (contractor rates)</span>
                  <strong className="text-red-600">$400,000</strong>
                </div>
                <div className="flex justify-between pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Project management</span>
                  <strong className="text-red-600">$40,000</strong>
                </div>
                <div className="flex justify-between pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Timeline (12 months)</span>
                  <strong className="text-red-600">12 months</strong>
                </div>
                <div className="flex justify-between pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Carrying costs</span>
                  <strong className="text-red-600">$180,000</strong>
                </div>
                <div className="flex justify-between pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Financing (8% interest)</span>
                  <strong className="text-red-600">$59,200</strong>
                </div>
                <div className="flex justify-between pt-4 border-t-2 border-red-400">
                  <span className="text-xl font-bold">Total Cost</span>
                  <strong className="text-2xl text-red-600">$979,200</strong>
                </div>
              </div>
            </div>

            {/* Professional Investor */}
            <div className="bg-white rounded-xl p-8 shadow-lg border-2 border-teal-200">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center">
                  <i className="ri-building-line text-2xl text-white"></i>
                </div>
                <h4 className="text-2xl font-bold">Professional Investor</h4>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Materials (wholesale pricing)</span>
                  <strong className="text-teal-600">$180,000</strong>
                </div>
                <div className="flex justify-between pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Labor (in-house crews)</span>
                  <strong className="text-teal-600">$240,000</strong>
                </div>
                <div className="flex justify-between pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Project management</span>
                  <strong className="text-teal-600">$15,000</strong>
                </div>
                <div className="flex justify-between pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Timeline (6 months)</span>
                  <strong className="text-teal-600">6 months</strong>
                </div>
                <div className="flex justify-between pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Carrying costs</span>
                  <strong className="text-teal-600">$90,000</strong>
                </div>
                <div className="flex justify-between pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Financing (4% interest)</span>
                  <strong className="text-teal-600">$17,400</strong>
                </div>
                <div className="flex justify-between pt-4 border-t-2 border-teal-400">
                  <span className="text-xl font-bold">Total Cost</span>
                  <strong className="text-2xl text-teal-600">$542,400</strong>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 text-center">
            <p className="text-2xl font-bold text-gray-800 mb-4">
              The investor completes the same renovation for <span className="text-teal-600">$436,800 less</span>
            </p>
            <p className="text-lg text-gray-700">
              That's a 45% cost savings, plus they finish in half the time. This is why selling AS IS to investors makes financial sense.
            </p>
          </div>
        </div>

        {/* Image */}
        <div className="rounded-2xl overflow-hidden shadow-xl">
          <img
            src="https://public.readdy.ai/ai/img_res/4ac0993eee2eb615d45fe243bb8a5d92.jpg"
            alt="Professional investors managing apartment renovations"
            className="w-full h-[500px] object-cover object-top"
          />
        </div>
      </div>
    </section>
  );
}